import { useState, useEffect } from 'react';
import { Box, Button, IconButton, Typography, Collapse } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import billingService from '../services/billingService';
import { useUserStore } from '../store/userStore';
import { useUserContext } from '../hooks/useUserContext';
import { config } from '../config';

const DEFAULT_TENANT_ID = config.defaultTenantId;

export default function SubscriptionBanner() {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUserStore();
  const { backendProfile, isTenantAdmin } = useUserContext();
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const isOrgMember = !!backendProfile?.tenantId && backendProfile.tenantId !== DEFAULT_TENANT_ID;

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    let cancelled = false;
    billingService.getUserSubscriptionStatus().then(status => {
      if (!cancelled) setHasSubscription(status.hasActiveSubscription);
    }).catch(() => {
      if (!cancelled) setHasSubscription(true); // fail open — don't spam on error
    });

    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn]);

  // Tenant members can't manage the tenant subscription, so they get an informational
  // "contact your administrator" variant instead of the purchase CTA.
  const isMemberNotice = isOrgMember && !isTenantAdmin;
  const visible = !dismissed && hasSubscription === false;

  return (
    <Collapse in={visible} unmountOnExit>
      <Box
        role="banner"
        aria-label="Subscription required"
        sx={(theme) => ({
          position: 'relative',
          background: `linear-gradient(100deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 45%, ${theme.palette.info.dark} 100%)`,
          overflow: 'hidden',
        })}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 1 },
            minHeight: { xs: 'auto', sm: 52 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          {/* Left: icon + copy */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)',
                flexShrink: 0,
              }}
            >
              <SchoolIcon sx={{ fontSize: 18, color: 'white' }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: '"Roboto", sans-serif',
                  fontSize: { xs: '13px', sm: '14px' },
                  fontWeight: 600,
                  color: 'white',
                  lineHeight: 1.3,
                  letterSpacing: '0.01em',
                }}
              >
                {isMemberNotice ? 'Subscription inactive.' : 'Start learning today.'}
                <Typography
                  component="span"
                  sx={{
                    display: { xs: 'none', sm: 'inline' },
                    fontWeight: 400,
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.82)',
                    ml: 1,
                  }}
                >
                  {isMemberNotice
                    ? 'Contact your administrator to restore course access.'
                    : 'Subscribe to unlock CPE courses.'}
                </Typography>
              </Typography>
              <Typography
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.82)',
                  mt: 0.25,
                }}
              >
                {isMemberNotice
                  ? 'Contact your administrator to restore course access.'
                  : 'Subscribe to unlock CPE courses.'}
              </Typography>
            </Box>
          </Box>

          {/* Right: CTA + dismiss */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 'auto', sm: 0 } }}>
            {!isMemberNotice && (
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: '15px !important' }} />}
              onClick={() => navigate(isTenantAdmin ? '/app/tenant-admin/billing' : '/app/billing')}
              sx={{
                bgcolor: '#ffc107',
                color: '#0c200f',
                fontWeight: 700,
                fontSize: '13px',
                letterSpacing: '0.02em',
                textTransform: 'none',
                borderRadius: '6px',
                px: 2,
                py: 0.75,
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  bgcolor: '#ffca28',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                },
                transition: 'background-color 0.15s, box-shadow 0.15s',
              }}
            >
              View Plans
            </Button>
            )}

            <IconButton
              size="small"
              aria-label="Dismiss subscription banner"
              onClick={() => setDismissed(true)}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                p: 0.5,
                '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
}
